import React from 'react';
import PropTypes from 'prop-types';
import './StateSelector.scss';

const StateSelector = ({
  options = [],
  value,
  onChange,
  disabled = false,
  size = 'medium',
  color = 'primary',
  variant = 'skeuomorphic',
  className = '',
  tooltip,
  animated = true,
  multiSelect = false,
  allowEmpty = false,
  id
}) => {
  const selectorId = id || `state-selector-${Math.random().toString(36).substr(2, 9)}`;

  const handleOptionClick = (optionValue) => {
    if (disabled || !onChange) return;

    if (multiSelect) {
      // Mode multi-sélection
      const currentValues = Array.isArray(value) ? value : [];
      let newValues;

      if (currentValues.includes(optionValue)) {
        // Désélectionner
        newValues = currentValues.filter(v => v !== optionValue);
      } else {
        // Sélectionner
        newValues = [...currentValues, optionValue];
      }

      onChange(newValues);
    } else {
      // Mode sélection simple
      if (value === optionValue && allowEmpty) {
        // Permettre de désélectionner si allowEmpty est true
        onChange(null);
      } else {
        onChange(optionValue);
      }
    }
  };

  const isSelected = (optionValue) => {
    if (multiSelect) {
      return Array.isArray(value) && value.includes(optionValue);
    }
    return value === optionValue;
  };

  const containerClasses = [
    'state-selector-container',
    `selector-${size}`,
    `selector-${variant}`,
    `selector-${color}`,
    disabled ? 'selector-disabled' : '',
    multiSelect ? 'selector-multi' : 'selector-single',
    animated ? 'selector-animated' : '',
    className
  ].filter(Boolean).join(' ');

  return (
    <div className={containerClasses} title={tooltip} id={selectorId}>
      <div className="state-selector-options">
        {options.map((option, index) => {
          const optionValue = typeof option === 'object' ? option.value : option;
          const optionLabel = typeof option === 'object' ? option.label : option;
          const optionColor = typeof option === 'object' ? option.color : color;
          const optionIcon = typeof option === 'object' ? option.icon : null;
          const selected = isSelected(optionValue);

          return (
            <button
              key={`${optionValue}-${index}`}
              type="button"
              className={`state-option state-option-${optionColor} ${selected ? 'active' : ''}`}
              onClick={() => handleOptionClick(optionValue)}
              disabled={disabled}
              aria-pressed={selected}
              aria-label={`${selected ? 'Deselect' : 'Select'} ${optionLabel}`}
            >
              {optionIcon && (
                <span className="option-icon">
                  {typeof optionIcon === 'function' ? optionIcon(selected) : optionIcon}
                </span>
              )}
              <span className="option-text">{optionLabel}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

StateSelector.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        value: PropTypes.any.isRequired,
        label: PropTypes.string.isRequired,
        color: PropTypes.oneOf(['primary', 'secondary', 'success', 'warning', 'error', 'info']),
        icon: PropTypes.oneOfType([PropTypes.node, PropTypes.func])
      })
    ])
  ).isRequired,
  value: PropTypes.oneOfType([
    PropTypes.any,
    PropTypes.arrayOf(PropTypes.any)
  ]),
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  color: PropTypes.oneOf(['primary', 'secondary', 'success', 'warning', 'error', 'info']),
  variant: PropTypes.oneOf(['skeuomorphic', 'flat', 'glass']),
  className: PropTypes.string,
  tooltip: PropTypes.string,
  animated: PropTypes.bool,
  multiSelect: PropTypes.bool,
  allowEmpty: PropTypes.bool,
  id: PropTypes.string
};

export default StateSelector;
